import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Button } from '@fikasio/react-ui-components';
import {
  useRequestOtpMutation,
  useVerifyOtpMutation,
} from 'services/login/api';
import { useAuth } from 'services/login/hooks';
import { setCredentials } from 'services/login/slice';
import links from 'utils/links';
import { setLanguage } from 'utils/translation';
import OtpInput from 'components/UI/OtpInput';
import styles from './SignupPage.module.css';

const SignupPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const [showSignupError, setShowSignupError] = useState(false);
  const [name, setName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [code, setCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  const [requestOtp] = useRequestOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, []);

  useEffect(() => {
    if (auth.user) {
      if (auth.user.language) {
        setLanguage(auth.user.language);
      }
      navigate(location.state ? location.state.from : links.paths.home);
    }
  }, [auth]);

  const handleRequestOtp = async () => {
    if (!name || !emailAddress) {
      setShowSignupError(true);
      return;
    }
    setIsSubmitting(true);
    setShowSignupError(false);
    try {
      await requestOtp({
        emailAddress,
        name,
        purpose: 'signup',
      }).unwrap();
      setOtpSent(true);
    } catch (error) {
      setShowSignupError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    const normalizedCode = code.trim().toUpperCase();
    if (!emailAddress || !normalizedCode) {
      setShowSignupError(true);
      return;
    }
    setIsSubmitting(true);
    setShowSignupError(false);
    try {
      const user = await verifyOtp({
        emailAddress,
        code: normalizedCode,
      }).unwrap();
      if (!user) {
        throw new Error('Signup failed');
      }
      dispatch(setCredentials({ user }));
      navigate(location.state ? location.state.from : links.paths.home);
    } catch (error) {
      setShowSignupError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    if (otpSent) {
      handleVerifyOtp();
    } else {
      handleRequestOtp();
    }
  };

  return (
    <div className={styles.Signup}>
      <Helmet>
        <title>{t('signup')}</title>
      </Helmet>
      <h4>{t('signup')}</h4>
      <div className={styles.layout}>
        {
          showSignupError && (
            <Alert variant="danger" >
              <strong>{t('invalidSubmission')}</strong>
            </Alert>
          )
        }
        {
          otpSent && !showSignupError && (
            <Alert variant="success">
              {t('otpSent')}
            </Alert>
          )
        }
        <form id="Signup_form" onSubmit={handleSubmit}>
          <div>
            <Form.Control
              autoFocus
              disabled={otpSent}
              name="name"
              onChange={e => setName(e.target.value)}
              placeholder={t('name')}
              style={{ marginBottom: 10 }}
              type="text"
              value={name}
            />
          </div>
          <div>
            <Form.Control
              disabled={otpSent}
              name="emailAddress"
              onChange={e => setEmailAddress(e.target.value)}
              placeholder={t('emailAddress')}
              style={{ marginBottom: 10 }}
              type="email"
              value={emailAddress}
            />
          </div>
          {
            otpSent && (
              <div>
                <OtpInput
                  autoFocus
                  disabled={isSubmitting}
                  onChange={setCode}
                  value={code}
                />
              </div>
            )
          }
          <Button.Action
            disabled={isSubmitting}
          >
            {otpSent ? t('verifyCode') : t('sendCode')}
          </Button.Action>
          {
            otpSent && (
              <>
                &nbsp;
                <Button.Action
                  disabled={isSubmitting}
                  onClick={e => {
                    e.preventDefault();
                    handleRequestOtp();
                  }}
                >
                  {t('resendCode')}
                </Button.Action>
              </>
            )
          }
        </form>
        <div style={{ marginTop: 10 }}>
          {t('or')}
          &nbsp;
          <Link to={links.paths.login}>
            {t('login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
